import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { mapAuthError } from '@mybusinessmate/api-client';
import { acceptInviteInputSchema } from '@mybusinessmate/schema';
import { AuthShell, INVITE_BRAND } from '../../../../components/AuthShell';
import { AuthHeading } from '../../../../components/auth/auth-heading';
import { Banner } from '../../../../components/ui/banner';
import { Button, buttonClassName } from '../../../../components/ui/button';
import { PasswordInput } from '../../../../components/ui/input';
import { Mascot } from '../../../../components/ui/mascot';
import { PasswordStrength } from '../../../../components/ui/password-strength';
import { StatusTag } from '../../../../components/ui/status-tag';
import { authClient } from '../../../../lib/api';
import { passwordStrength } from '../../../../lib/password-strength';

// The invite email links here via Better Auth's reset-password redirect: `?token=...` when the link is good,
// `?error=INVALID_TOKEN` when it is expired or already used.
export const Route = createFileRoute('/subdomain/admin/_auth/invite')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' && search.token ? search.token : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  component: InvitePage,
});

type Stage = 'form' | 'expired' | 'done';

function InvitePage() {
  const { token, error: linkError } = Route.useSearch();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>(!token || linkError ? 'expired' : 'form');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setProblem(undefined);
    const parsed = acceptInviteInputSchema.safeParse({ token, password, confirmPassword: confirm });
    if (!parsed.success) return setProblem(parsed.error.issues[0]?.message);
    setBusy(true);
    const { error } = await authClient.resetPassword({ newPassword: password, token: token! });
    setBusy(false);
    const mapped = mapAuthError(error);
    if (mapped) {
      if (mapped.kind === 'invalid_token') return setStage('expired');
      return setProblem(mapped.message);
    }
    setStage('done');
  };

  if (stage === 'expired') {
    return (
      <AuthShell brand={INVITE_BRAND}>
        <Mascot
          pose="think"
          className="h-(--mbm-size-mascot-height) w-(--mbm-size-mascot-width) self-center lg:hidden"
        />
        <AuthHeading
          title="This invite has expired"
          // TODO(product): the design says invite links last 72 hours; they currently share the password-reset
          // lifetime (15 minutes). Update this sentence if the lifetime changes.
          subtitle="Invite links only work for 15 minutes. Ask your admin to send you a new one."
        />
        <Link to="/subdomain/admin/admin-login" className={buttonClassName({ fullWidth: true })}>
          Back to staff sign in
        </Link>
      </AuthShell>
    );
  }

  if (stage === 'done') {
    return (
      <AuthShell brand={{ ...INVITE_BRAND, mascot: 'cheer' }}>
        <StatusTag label="Password saved" tone="green" />
        <AuthHeading
          title="You’re all set"
          subtitle="Your password is saved. From now on, sign in with your work email."
        />
        <Button fullWidth onClick={() => navigate({ to: '/subdomain/admin/admin-login' })}>
          Sign in to continue
        </Button>
      </AuthShell>
    );
  }

  const matches = confirm.length > 0 && password === confirm;
  return (
    <AuthShell brand={INVITE_BRAND}>
      {/* TODO(api): the design greets the invitee by name and shows their role, department and work email. No endpoint
          returns invite details for a token yet, so the heading is generic and the email field is omitted. */}
      <AuthHeading title="Join the team" subtitle="Set a password to start." />
      {problem ? <Banner tone="error">{problem}</Banner> : null}
      <div className="flex flex-col gap-(--mbm-space-3)">
        <PasswordInput
          label="Create password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordStrength level={passwordStrength(password)} />
      </div>
      <PasswordInput
        label="Confirm password"
        autoComplete="new-password"
        value={confirm}
        helperText={matches ? 'Passwords match' : undefined}
        error={confirm.length > 0 && !matches ? 'Passwords do not match' : undefined}
        onChange={(e) => setConfirm(e.target.value)}
      />
      <Button fullWidth loading={busy} disabled={!password || !matches} onClick={() => void submit()}>
        Set password & continue
      </Button>
    </AuthShell>
  );
}
